import { paths } from "api/schema/api-schema";
import { ToolSpecV2 } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { createContext, ReactNode, useContext } from "react";
import { ToolSpecV3 } from "types/json-schemas/ToolSpecV3";

type ToolDetails =
  paths["/trs/tool/{id}/"]["get"]["responses"]["200"]["content"]["application/json"];

/**
 * Necessary ts magic to prevent narrowing to revert back to unknown later
 */

type VersionWithoutToolSpecs = Omit<
  ToolDetails["tool_versions"][number],
  "tool_spec" | "draft_tool_spec"
>;

type ToolWithoutVersions = Omit<ToolDetails, "tool_versions">;

export type ToolDataContextValue = {
  tool: ToolWithoutVersions & {
    tool_versions: (VersionWithoutToolSpecs & {
      tool_spec: ToolSpecV2 | ToolSpecV3 | null;
      draft_tool_spec: ToolSpecV3 | null;
    })[];
  };
  invalidateToolQuery: () => Promise<void>;
};

const ToolDataContext = createContext<ToolDataContextValue | undefined>(
  undefined,
);

interface ToolDataProviderProps {
  children: ReactNode;
  tool: ToolDataContextValue["tool"];
  invalidateToolQuery: () => Promise<void>;
}

export const ToolDataProvider = ({
  children,
  tool,
  invalidateToolQuery,
}: ToolDataProviderProps) => {
  return (
    <ToolDataContext.Provider
      value={{
        tool,
        invalidateToolQuery,
      }}
    >
      {children}
    </ToolDataContext.Provider>
  );
};

export const useToolDataContext = () => {
  const value = useContext(ToolDataContext);

  if (value === undefined) {
    throw new ContextOutOfBoundsError("ToolDataContext");
  }

  return value;
};
